import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { Minus, Plus, X, Tag } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, updateQty, subtotal } = useCart();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(null);
  const nav = useNavigate();

  const applyCoupon = async () => {
    try {
      const { data } = await api.get(`/coupons/validate/${coupon}`);
      setDiscount(subtotal * (data.percent_off / 100));
      setCouponApplied(data.code);
      toast.success(`${data.percent_off}% off applied`);
    } catch { toast.error("Invalid coupon"); }
  };

  const total = Math.max(0, subtotal - discount);

  if (items.length === 0) {
    return (
      <div className="container-luxe py-32 text-center">
        <h1 className="font-display text-4xl font-semibold">Your cart is empty</h1>
        <p className="text-gray-400 mt-4">Explore our curated selections.</p>
        <Link to="/shop" className="btn-gold mt-8">Discover Shop</Link>
      </div>
    );
  }

  return (
    <div className="container-luxe py-16">
      <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mb-12">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`} className="glass rounded-xl p-4 flex gap-4 items-center">
              {it.image && <img src={it.image} alt={it.name} className="w-24 h-24 object-cover rounded-lg" />}
              <div className="flex-1">
                <div className="font-display text-lg">{it.name}</div>
                <div className="text-[#D4AF37] mt-1">${it.price.toFixed(2)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateQty(it.product_id, Math.max(1, it.quantity - 1))} className="w-8 h-8 border border-white/10 rounded flex items-center justify-center"><Minus size={14} /></button>
                <span className="w-8 text-center">{it.quantity}</span>
                <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-8 h-8 border border-white/10 rounded flex items-center justify-center"><Plus size={14} /></button>
              </div>
              <button data-testid={`remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="text-gray-500 hover:text-red-400"><X size={18} /></button>
            </div>
          ))}
        </div>
        <div className="glass rounded-2xl p-8 h-fit">
          <h3 className="font-display text-xl mb-6">Summary</h3>
          <div className="flex justify-between text-sm mb-3"><span className="text-gray-400">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          {discount > 0 && <div className="flex justify-between text-sm mb-3"><span className="text-[#00A86B]">Discount ({couponApplied})</span><span className="text-[#00A86B]">−${discount.toFixed(2)}</span></div>}
          <div className="flex justify-between text-sm mb-3"><span className="text-gray-400">Tax</span><span>Included</span></div>
          <div className="border-t border-white/10 my-4"></div>
          <div className="flex justify-between text-lg font-display mb-6"><span>Total</span><span className="text-[#D4AF37]">${total.toFixed(2)}</span></div>
          <div className="flex gap-2 mb-4">
            <input data-testid="coupon-input" placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
            <button data-testid="apply-coupon" onClick={applyCoupon} className="btn-ghost text-sm px-4"><Tag size={14} /></button>
          </div>
          <button data-testid="checkout-btn" onClick={() => nav("/checkout", { state: { coupon: couponApplied } })} className="btn-gold w-full justify-center">Proceed to Checkout</button>
        </div>
      </div>
    </div>
  );
}
